import { useCallback, useState } from 'react';

const STORAGE_KEY = 'lexora:videos:recent-searches';
const MAX_ENTRIES = 6;

const readStored = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
  } catch {
    return [];
  }
};

const writeStored = (entries: string[]) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage can be unavailable (private browsing, quota) — recent searches are a convenience, not critical.
  }
};

/** Most-recent-first list of past video-transcript searches, kept in this browser only. */
export function useRecentVideoSearches() {
  const [recentSearches, setRecentSearches] = useState<string[]>(readStored);

  const addSearch = useCallback((query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setRecentSearches((current) => {
      const withoutDuplicate = current.filter((entry) => entry.toLowerCase() !== trimmed.toLowerCase());
      const next = [trimmed, ...withoutDuplicate].slice(0, MAX_ENTRIES);
      writeStored(next);
      return next;
    });
  }, []);

  return { recentSearches, addSearch };
}
